import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { loaderDecrement, loaderIncrement } from "../services/globalLoader";

const formatInr = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0
  }).format(Number(n || 0));

const monthName = (m) => {
  const n = Number(m);
  if (!n || n < 1 || n > 12) return "—";
  return new Date(2000, n - 1, 1).toLocaleString("en-IN", { month: "long" });
};

function receiptUrl(ref, rentProofPublicBaseUrl) {
    if (!ref || typeof ref !== "object") return null;
    if (ref.downloadUrl) return ref.downloadUrl;
    if (ref.url) return ref.url;
    const base = rentProofPublicBaseUrl && String(rentProofPublicBaseUrl).replace(/\/$/, "");
    const key = ref.s3Key && String(ref.s3Key).replace(/^\//, "");
    if (base && key) {
      const parts = key.split("/").filter(Boolean);
      return `${base}/${parts.map(encodeURIComponent).join("/")}`;
    }
    return null;
  }

const payStatusStyle = (raw) => {
  const s = String(raw || "").toLowerCase();
  if (s === "paid" || s === "completed" || s === "success") {
    return { bg: "rgba(34,197,94,0.18)", color: "#16a34a" };
  }
  if (s === "pending" || s === "due") {
    return { bg: "rgba(234,179,8,0.2)", color: "#a16207" };
  }
  if (s === "failed" || s === "overdue") {
    return { bg: "rgba(239,68,68,0.18)", color: "#dc2626" };
  }
  return { bg: "rgba(59,130,246,0.15)", color: "#2563eb" };
};

const PaymentView = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const { id: idParam } = useParams();

  const userId = useMemo(() => {
    try {
      return idParam ? decodeURIComponent(String(idParam)).trim() : "";
    } catch {
      return String(idParam || "").trim();
    }
  }, [idParam]);

  const [loading, setLoading] = useState(true);
  const [dueInfo, setDueInfo] = useState(null);

  useEffect(() => {
    if (!userId) {
      toast.error("Invalid payment details link.");
      navigate("/payment", { replace: true });
    }
  }, [userId, navigate]);

  const fetchDueAmount = useCallback(async () => {
    if (!API_BASE_URL || !userId) {
      if (!API_BASE_URL) toast.error("Missing REACT_APP_API_URL");
      setLoading(false);
      return;
    }

    let didIncrement = false;
    try {
      loaderIncrement();
      didIncrement = true;
      const response = await axios.post(`${API_BASE_URL}`, {
        action: "tenant.getDueAmount",
        userId
      });
      const data = response?.data;

      if (data?.status === "success" && data?.dueInfo) {
        setDueInfo(data.dueInfo);
      } else {
        toast.error(data?.message || "Could not load due amount");
        setDueInfo(null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Something went wrong");
      setDueInfo(null);
    } finally {
      if (didIncrement) loaderDecrement();
      setLoading(false);
    }
  }, [API_BASE_URL, userId]);

  useEffect(() => {
    if (!userId) return;
    fetchDueAmount();
  }, [userId, fetchDueAmount]);

  const tenant = dueInfo?.tenant;
  const period = dueInfo?.currentPeriod;
  const periodLabel =
    period?.month && period?.year
      ? `${monthName(period.month)} ${period.year}`
      : "—";

  const payments = Array.isArray(dueInfo?.payments) ? dueInfo.payments : [];
  const baseUrl = dueInfo?.rentProofPublicBaseUrl;

  if (!userId) {
    return null;
  }

  return (
    <div className="employee-form tenant-report">
      <div className="content-header">
        <div className="container-fluid">
          <div
            className="d-flex justify-content-between align-items-center flex-wrap"
            style={{ gap: 12 }}
          >
            <div>
              <h1 className="m-0 txt_lft">Payment details</h1>
              <p style={{ margin: 0, color: "#6c757d", fontSize: "14px" }}>
                Due amount, current period, and payment records
              </p>
            </div>
            <div className="d-flex flex-wrap" style={{ gap: 8 }}>
             
              <Link to="/payment" className="btn btn-outline-secondary">
                Back
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          {loading && !dueInfo ? (
            <div className="card">
              <div className="card-body text-muted">Loading…</div>
            </div>
          ) : !dueInfo ? (
            <div className="card">
              <div className="card-body text-muted">No data available.</div>
            </div>
          ) : (
            <>
              <div className="row" style={{ marginBottom: 16 }}>
                <div className="col-lg-6 mb-3 mb-lg-0">
                  <div className="card h-100">
                    <div className="card-body">
                      <h5
                        className="card-title"
                        style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}
                      >
                        Tenant
                      </h5>
                      <dl className="row mb-0" style={{ fontSize: 14 }}>
                        <dt className="col-sm-4 text-muted">Name</dt>
                        <dd className="col-sm-8 mb-2">{tenant?.name || "—"}</dd>
                        <dt className="col-sm-4 text-muted">Email</dt>
                        <dd className="col-sm-8 mb-2">{tenant?.email || "—"}</dd>
                        <dt className="col-sm-4 text-muted">Flat</dt>
                        <dd className="col-sm-8 mb-2">{tenant?.flatNumber || "—"}</dd>
                        <dt className="col-sm-4 text-muted">Monthly rent</dt>
                        <dd className="col-sm-8 mb-0">
                          <strong>{formatInr(tenant?.monthlyRent)}</strong>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
                <div className="col-lg-6">
                  <div className="card h-100">
                    <div className="card-body">
                      <h5
                        className="card-title"
                        style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}
                      >
                        Current period &amp; status
                      </h5>
                      <p className="mb-2" style={{ fontSize: 14 }}>
                        <span className="text-muted">Billing period: </span>
                        <strong>{periodLabel}</strong>
                      </p>
                      <p className="mb-3" style={{ fontSize: 14 }}>
                        <span className="text-muted">Pending: </span>
                        <strong>{formatInr(dueInfo.pendingAmount)}</strong>
                      </p>
                      <div className="d-flex flex-wrap align-items-center" style={{ gap: 10 }}>
                        <span
                          className="badge"
                          style={{
                            padding: "0.4rem 0.75rem",
                            borderRadius: 999,
                            fontWeight: 600,
                            fontSize: 12,
                            textTransform: "capitalize",
                            background: dueInfo.isPaidForCurrentMonth
                              ? "rgba(34,197,94,0.18)"
                              : "rgba(234,179,8,0.25)",
                            color: dueInfo.isPaidForCurrentMonth ? "#16a34a" : "#a16207"
                          }}
                        >
                          {dueInfo.isPaidForCurrentMonth
                            ? "Paid for current month"
                            : "Not paid for current month"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-body">
                  <h5
                    className="card-title"
                    style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}
                  >
                    Payment records
                  </h5>
                  <div className="table-responsive">
                    <table className="table table-hover role-table mb-0">
                      <thead>
                        <tr>
                          <th>
                            <span className="role-th">Period</span>
                          </th>
                          <th>
                            <span className="role-th">Rent</span>
                          </th>
                          <th>
                            <span className="role-th">Status</span>
                          </th>
                          <th>
                            <span className="role-th">Notes</span>
                          </th>
                          <th>
                            <span className="role-th">Receipts</span>
                          </th>
                          <th>
                            <span className="role-th">Updated</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {payments.length ? (
                          payments.map((p) => {
                            const st = payStatusStyle(p.paymentStatus);
                            const refs = Array.isArray(p.paymentReference)
                              ? p.paymentReference
                              : [];
                            return (
                              <tr key={p._id || `${p.month}-${p.year}`}>
                                <td>
                                  {formatPeriodLabel(p.month, p.year)}
                                </td>
                                <td>{formatInr(p.rentAmount)}</td>
                                <td>
                                  <span
                                    className="badge"
                                    style={{
                                      padding: "0.35rem 0.65rem",
                                      borderRadius: 999,
                                      fontWeight: 600,
                                      fontSize: 11,
                                      background: st.bg,
                                      color: st.color,
                                      textTransform: "capitalize"
                                    }}
                                  >
                                    {p.paymentStatus || "—"}
                                  </span>
                                </td>
                                <td className="text-muted" style={{ maxWidth: 200 }}>
                                  {p.notes?.trim() ? p.notes : "—"}
                                </td>
                                <td>
                                  {refs.length ? (
                                    <ul className="mb-0 ps-3" style={{ fontSize: 13 }}>
                                      {refs.map((ref, i) => {
                                        const href = receiptUrl(ref, baseUrl);
                                        const label = ref.originalFileName || `File ${i + 1}`;
                                        return (
                                          <li key={ref.s3Key || i}>
                                            {href ? (
                                              <a
                                                href={href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                              >
                                                {label}
                                              </a>
                                            ) : (
                                              label
                                            )}
                                          </li>
                                        );
                                      })}
                                    </ul>
                                  ) : (
                                    <span className="text-muted">—</span>
                                  )}
                                </td>
                                <td className="text-muted" style={{ fontSize: 12 }}>
                                  {formatDateShort(p.updatedAt || p.createdAt)}
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan={6} className="text-center text-muted">
                              No payment rows yet
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

function formatPeriodLabel(month, year) {
  const y = year != null ? String(year) : "";
  const m = monthName(month);
  return y ? `${m} ${y}` : m;
}

function formatDateShort(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  } catch {
    return String(iso);
  }
}

export default PaymentView;
